/** O esquema IFC (IFC2X3, IFC4, IFC4X3) tal como o web-ifc o traz:
 *  nomes e ordem dos atributos de cada classe, atributos inversos e
 *  herança. Nada vem da rede: são as tabelas geradas a partir dos EXPRESS
 *  da buildingSMART que o web-ifc (MPL-2.0) já inclui e que o viewer já
 *  carrega.
 *
 *  Os nomes dos atributos saem de `ToRawLineData`, a função que o web-ifc
 *  usa para escrever cada classe em STEP: chama-se com um objecto que
 *  devolve, para cada propriedade lida, uma marca com o nome. A posição
 *  de cada marca no array é a posição do atributo na linha STEP. Uma
 *  posição sem marca é um atributo derivado (escrito `*` no ficheiro).
 *  Não depende de nomes de parâmetros, que a minificação apaga.
 *
 *  Determinismo: usa-se o esquema que o FILE_SCHEMA do ficheiro declara;
 *  sem esquema reconhecido não se escolhe nenhum, e os atributos ficam
 *  numerados. */

import * as WEBIFC from 'web-ifc'

type Tabela<T> = Record<number, T>
const W = WEBIFC as unknown as {
  SchemaNames: (string[] | null)[]
  ToRawLineData: Tabela<Tabela<(i: unknown) => unknown[]>>
  InversePropertyDef: Tabela<Tabela<[string, number, number, boolean][]>>
  InheritanceDef: Tabela<Tabela<number[]>>
  IFC2X3: Record<string, unknown>
  IFC4: Record<string, unknown>
  IFC4X3: Record<string, unknown>
} & Record<string, unknown>

/** 1 = IFC2X3, 2 = IFC4, 3 = IFC4X3 (os índices do web-ifc), ou null. */
export function indiceDoEsquema(esquema: string | null | undefined): number | null {
  if (!esquema) return null
  const e = esquema.toUpperCase()
  const i = W.SchemaNames.findIndex((ns) => ns?.includes(e))
  return i > 0 ? i : null
}

let codigos: Map<string, number> | null = null
let nomesPorCodigo: Map<number, string> | null = null
let camel: Map<string, string> | null = null

function tabelasDeNomes() {
  if (!codigos) {
    codigos = new Map()
    nomesPorCodigo = new Map()
    for (const k of Object.keys(W)) {
      const v = W[k]
      if (typeof v === 'number' && /^IFC[A-Z0-9_]+$/.test(k)) {
        codigos.set(k, v)
        nomesPorCodigo.set(v, k)
      }
    }
    camel = new Map()
    for (const ns of [W.IFC2X3, W.IFC4, W.IFC4X3]) for (const k of Object.keys(ns)) camel.set(k.toUpperCase(), k)
  }
  return { codigos: codigos!, nomesPorCodigo: nomesPorCodigo!, camel: camel! }
}

/** "IFCWALLSTANDARDCASE" -> "IfcWallStandardCase" (ou o que veio, se não se conhece). */
export function nomeDaClasse(classe: string): string {
  return tabelasDeNomes().camel.get(classe.toUpperCase()) ?? classe
}

function codigoDe(classe: string): number | undefined {
  return tabelasDeNomes().codigos.get(classe.toUpperCase())
}

function classeDoCodigo(codigo: number): string | undefined {
  return tabelasDeNomes().nomesPorCodigo.get(codigo)
}

const MARCA = Symbol('atributo')
interface Marca {
  [MARCA]: string
  label: string
  value: Marca
  map: (cb: (x: Marca) => unknown) => unknown[]
}

function marca(nome: string): Marca {
  const m = {
    [MARCA]: nome,
    // O Labelise do web-ifc deixa passar o que tem `label`.
    label: nome,
    map(cb: (x: Marca) => unknown) {
      return [cb(m)]
    },
  } as unknown as Marca
  // Os booleanos escrevem-se `{ type: 3, value: i.X.value }`.
  Object.defineProperty(m, 'value', { get: () => m })
  return m
}

function nomeNaMarca(x: unknown): string | null {
  if (Array.isArray(x)) return nomeNaMarca(x[0])
  if (x && typeof x === 'object') {
    if (MARCA in x) return (x as Marca)[MARCA]
    const v = (x as { value?: unknown }).value
    if (v && typeof v === 'object' && MARCA in v) return (v as Marca)[MARCA]
  }
  return null
}

const cacheAtributos = new Map<string, (string | null)[] | null>()

/** Os atributos da classe, pela ordem da linha STEP. `null` numa posição
 *  é um atributo derivado. Devolve null se o esquema ou a classe não se
 *  conhecem. */
export function atributosDe(esquema: string | null | undefined, classe: string): (string | null)[] | null {
  const s = indiceDoEsquema(esquema)
  const c = codigoDe(classe)
  if (s === null || c === undefined) return null
  const chave = `${s}:${c}`
  if (cacheAtributos.has(chave)) return cacheAtributos.get(chave)!
  let out: (string | null)[] | null = null
  const f = W.ToRawLineData[s]?.[c]
  if (f) {
    try {
      const arr = f(new Proxy({}, { get: (_, k) => marca(String(k)) }))
      out = arr.map(nomeNaMarca)
    } catch {
      out = null
    }
  }
  cacheAtributos.set(chave, out)
  return out
}

export interface AtributoInverso {
  nome: string
  /** Classe da relação (em maiúsculas), ou de uma superclasse dela. */
  relacao: string
  /** Posição, na relação, do atributo que aponta para esta entidade. */
  indice: number
  conjunto: boolean
}

export function inversosDe(esquema: string | null | undefined, classe: string): AtributoInverso[] {
  const s = indiceDoEsquema(esquema)
  const c = codigoDe(classe)
  if (s === null || c === undefined) return []
  return (W.InversePropertyDef[s]?.[c] ?? []).map(([nome, rel, indice, conjunto]) => ({
    nome,
    relacao: classeDoCodigo(rel) ?? String(rel),
    indice,
    conjunto,
  }))
}

/** A classe é ela própria ou descende de `antepassado`? */
export function eSubclasse(esquema: string | null | undefined, classe: string, antepassado: string): boolean {
  if (classe.toUpperCase() === antepassado.toUpperCase()) return true
  const s = indiceDoEsquema(esquema)
  const c = codigoDe(classe)
  const a = codigoDe(antepassado)
  if (s === null || c === undefined || a === undefined) return false
  return (W.InheritanceDef[s]?.[a] ?? []).includes(c)
}

const cacheAntepassados = new Map<string, string[]>()

/** Os antepassados da classe, da raiz para o pai (IfcRoot, IfcObjectDefinition, ...). */
export function antepassadosDe(esquema: string | null | undefined, classe: string): string[] {
  const s = indiceDoEsquema(esquema)
  const c = codigoDe(classe)
  if (s === null || c === undefined) return []
  const chave = `${s}:${c}`
  const visto = cacheAntepassados.get(chave)
  if (visto) return visto
  // A tabela do web-ifc diz, de cada classe, todos os descendentes.
  // Quem tem mais descendentes está mais acima.
  const tabela = W.InheritanceDef[s] ?? {}
  const pais: { classe: string; n: number }[] = []
  for (const [k, desc] of Object.entries(tabela)) {
    if (desc.includes(c)) pais.push({ classe: classeDoCodigo(Number(k)) ?? k, n: desc.length })
  }
  const out = pais.sort((a, b) => b.n - a.n).map((p) => p.classe)
  cacheAntepassados.set(chave, out)
  return out
}

/** Todas as classes com atributos conhecidos no esquema (em maiúsculas). */
export function classesDoEsquema(esquema: string | null | undefined): string[] {
  const s = indiceDoEsquema(esquema)
  if (s === null) return []
  return Object.keys(W.ToRawLineData[s] ?? {})
    .map((k) => classeDoCodigo(Number(k)))
    .filter((x): x is string => !!x)
}

/** O nome do atributo inverso que a referência de `origem` (no
 *  argumento `indice`) representa em `alvo`, se o esquema o declarar. */
export function nomeDoInverso(
  esquema: string | null | undefined,
  classeDoAlvo: string,
  classeDaOrigem: string,
  indice: number,
): string | null {
  for (const inv of inversosDe(esquema, classeDoAlvo)) {
    if (inv.indice === indice && eSubclasse(esquema, classeDaOrigem, inv.relacao)) return inv.nome
  }
  return null
}
